/* AUREME PDP の表示順・横スクロール・画像別モデルスペック・Judge.me 表示補助を扱う */
(() => {
  const namespace = window.AuremeProductDetail || {};
  const ACTIVE_MEDIA_CHANGE_EVENT = 'aureme:pdp:active-media-change';

  if (
    namespace.VariantPickerPlacementController &&
    namespace.ColorCardScrollController &&
    namespace.ModelSpecController &&
    namespace.ReviewSortLabelsController &&
    namespace.ReviewLoginPromptController
  ) {
    return;
  }

  const reviewSortLabels = {
    created_at: '新着順',
    highest_rating: '評価が高い順',
    lowest_rating: '評価が低い順',
    with_pictures: '画像ありのみ',
    pictures_first: '画像あり優先',
    videos_first: '動画あり優先',
    most_helpful: '参考になった順',
  };

  /*
   * SP/タブレットでは variant picker の DOM 位置も表示順に合わせる。
   * 元位置のコメントノードを目印にし、PC 復帰や section 破棄時に Liquid 初期位置へ戻す。
   */
  class VariantPickerPlacementController {
    constructor(host) {
      this.host = host;
      this.anchor = undefined;
      this.mediaQuery = undefined;
      this.handlePlacementChange = this.sync.bind(this);
    }

    get primaryContent() {
      return this.host.querySelector('.aureme-product-detail__primary');
    }

    get variantPickerPanel() {
      return this.host.querySelector('.aureme-product-detail__options');
    }

    connect() {
      if (this.anchor) return;

      const options = this.variantPickerPanel;
      if (!options) return;

      this.anchor = document.createComment('AUREME バリアントピッカー元位置');
      options.after(this.anchor);
      this.mediaQuery = window.matchMedia('(max-width: 989px)');
      this.mediaQuery.addEventListener('change', this.handlePlacementChange);
      this.sync();
    }

    disconnect() {
      this.mediaQuery?.removeEventListener('change', this.handlePlacementChange);
      this.restore();
      this.anchor?.remove();
      this.anchor = undefined;
      this.mediaQuery = undefined;
    }

    restore() {
      const options = this.variantPickerPanel;
      if (!options || !this.anchor?.isConnected) return;

      this.anchor.before(options);
    }

    sync() {
      const primary = this.primaryContent;
      const options = this.variantPickerPanel;
      if (!primary || !options || !this.anchor) return;

      if (this.mediaQuery?.matches) {
        primary.insertBefore(options, primary.firstElementChild);
        return;
      }

      this.restore();
    }
  }

  /* 選択中の色カードが見切れないよう、表示幅ごとに横位置だけを同期する */
  class ColorCardScrollController {
    constructor(host) {
      this.host = host;
    }

    get colorCardsContainer() {
      return this.host.querySelector(
        '.product-form__input--aureme-color .aureme-product-option__cards',
      );
    }

    get selectedColorInput() {
      return this.host.querySelector(
        '.product-form__input--aureme-color .aureme-color-card__input:checked',
      );
    }

    getScrollBehavior() {
      return window.matchMedia('(min-width: 990px)').matches ? 'smooth' : 'auto';
    }

    scrollSelectedColorCardIntoView(behavior = this.getScrollBehavior()) {
      this.scrollInputIntoView(this.selectedColorInput, behavior);
    }

    scrollInputIntoView(input, behavior = this.getScrollBehavior()) {
      const cards = input?.closest('.aureme-product-option__cards') || this.colorCardsContainer;
      const card = input?.nextElementSibling;
      if (!cards || !card) return;

      const maxScrollLeft = Math.max(cards.scrollWidth - cards.clientWidth, 0);
      const centeredScrollLeft = card.offsetLeft - (cards.clientWidth - card.offsetWidth) / 2;
      const nextScrollLeft = Math.min(Math.max(centeredScrollLeft, 0), maxScrollLeft);

      if (Math.abs(cards.scrollLeft - nextScrollLeft) < 1) return;

      /*
       * scrollIntoView はページ全体の縦スクロールにも影響するため、
       * 色カードコンテナ内の横位置だけを中央寄せで調整する。
       */
      cards.scrollTo({ left: nextScrollLeft, behavior });
    }
  }

  /* Judge.me のソート値を維持したまま、選択肢の表示名だけを同期する */
  class ReviewSortLabelsController {
    constructor(host) {
      this.host = host;
      this.observer = undefined;
      this.animationFrameId = undefined;
      this.handleMutations = this.scheduleSync.bind(this);
    }

    get reviewAccordion() {
      return this.host.querySelector('[data-aureme-review-accordion]');
    }

    connect() {
      if (this.observer) return;

      const reviewAccordion = this.reviewAccordion;
      if (!reviewAccordion) return;

      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(reviewAccordion, { childList: true, subtree: true });
      this.scheduleSync();
    }

    disconnect() {
      this.observer?.disconnect();
      this.observer = undefined;

      if (this.animationFrameId !== undefined) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = undefined;
      }
    }

    scheduleSync() {
      if (this.animationFrameId !== undefined) return;

      this.animationFrameId = requestAnimationFrame(() => {
        this.animationFrameId = undefined;
        this.sync();
      });
    }

    sync() {
      const sortSelect = this.reviewAccordion?.querySelector('select.jdgm-sort-dropdown');
      if (!sortSelect) return;

      for (const option of sortSelect.options) {
        const label = reviewSortLabels[option.value];
        // 自身の表示名更新も observer が検知するため、差分がある選択肢だけを書き換える。
        if (!label || option.textContent === label) continue;

        option.textContent = label;
      }
    }
  }

  /*
   * Judge.me の非同期描画や再描画後も、未ログイン導線を
   * ヘッダーとレビュー本文の間へ配置し直す。
   */
  class ReviewLoginPromptController {
    constructor(host) {
      this.host = host;
      this.prompt = undefined;
      this.observer = undefined;
      this.animationFrameId = undefined;
      this.handleMutations = this.scheduleSync.bind(this);
    }

    get reviewAccordion() {
      return this.host.querySelector('[data-aureme-review-accordion]');
    }

    get reviewBody() {
      return this.reviewAccordion?.querySelector('.jm-review-widget__body');
    }

    connect() {
      if (this.observer) return;

      this.prompt = this.host.querySelector('[data-aureme-review-login-prompt]');
      const reviewAccordion = this.reviewAccordion;
      if (!this.prompt || !reviewAccordion) return;

      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(reviewAccordion, { childList: true, subtree: true });
      this.scheduleSync();
    }

    disconnect() {
      this.observer?.disconnect();
      this.observer = undefined;

      if (this.animationFrameId !== undefined) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = undefined;
      }
    }

    scheduleSync() {
      if (this.animationFrameId !== undefined) return;

      this.animationFrameId = requestAnimationFrame(() => {
        this.animationFrameId = undefined;
        this.sync();
      });
    }

    sync() {
      const reviewBody = this.reviewBody;
      if (!this.prompt || !reviewBody) return;

      if (
        this.prompt.parentElement !== reviewBody.parentElement ||
        this.prompt.nextElementSibling !== reviewBody
      ) {
        reviewBody.before(this.prompt);
      }

      this.prompt.hidden = false;
    }
  }

  /* 選択画像の alt にモデルスペックがあれば、SIZE 内の代表画像由来表示を差し替える */
  class ModelSpecController {
    constructor(host) {
      this.host = host;
      this.productInfo = undefined;
      this.handleActiveMediaChange = this.sync.bind(this);
    }

    get sizeAccordion() {
      return this.host.querySelector('[data-aureme-size-accordion]');
    }

    get modelSpecElement() {
      return this.sizeAccordion?.querySelector('[data-aureme-model-spec-value]');
    }

    get sizeMeasurementsElement() {
      return this.sizeAccordion?.querySelector('[data-aureme-size-measurements]');
    }

    get activeMediaImage() {
      return this.productInfo?.querySelector(
        'media-gallery .product__media-item.is-active .product__media-image',
      );
    }

    connect() {
      if (this.productInfo) return;

      this.productInfo = this.host.closest('product-info');
      this.productInfo?.addEventListener(ACTIVE_MEDIA_CHANGE_EVENT, this.handleActiveMediaChange);
      this.sync();
    }

    disconnect() {
      this.productInfo?.removeEventListener(
        ACTIVE_MEDIA_CHANGE_EVENT,
        this.handleActiveMediaChange,
      );
      this.productInfo = undefined;
    }

    getActiveMediaModelSpec() {
      /* 初期表示と画像切替で解釈がずれないよう、Liquid と同じく alt の「,」区切り3番目（index 2）をモデルスペックとして扱う */
      const altTokens = String(this.activeMediaImage?.alt || '').split(',');
      return String(altTokens[2] || '').trim();
    }

    sync() {
      const modelSpecElement = this.modelSpecElement;
      if (!modelSpecElement) return;

      const defaultModelSpec = String(modelSpecElement.dataset.auremeDefaultModelSpec || '').trim();
      const modelSpec = this.getActiveMediaModelSpec() || defaultModelSpec;

      modelSpecElement.textContent = modelSpec ? `Model: ${modelSpec}` : '';
      modelSpecElement.classList.toggle('hidden', !modelSpec);

      const hasSizeMeasurements = Boolean(this.sizeMeasurementsElement?.textContent.trim());
      this.sizeAccordion?.classList.toggle('hidden', !hasSizeMeasurements && !modelSpec);
    }
  }

  namespace.VariantPickerPlacementController =
    namespace.VariantPickerPlacementController || VariantPickerPlacementController;
  namespace.ColorCardScrollController =
    namespace.ColorCardScrollController || ColorCardScrollController;
  namespace.ModelSpecController = namespace.ModelSpecController || ModelSpecController;
  namespace.ReviewSortLabelsController =
    namespace.ReviewSortLabelsController || ReviewSortLabelsController;
  namespace.ReviewLoginPromptController =
    namespace.ReviewLoginPromptController || ReviewLoginPromptController;
  window.AuremeProductDetail = namespace;
})();
